import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useAsync } from 'react-use'

import { Form as AntdForm, Card, Row, Col, Table, Modal, Input, Select, Button, Space, Popconfirm, Tag, Skeleton } from 'antd'
import { PlusOutlined, EyeOutlined, DeleteOutlined, CheckOutlined, CloseOutlined, SearchOutlined, PrinterOutlined } from '@ant-design/icons'

import { useAuth } from '../../contexts/AuthContextProvider'
import { useLayoutContext } from '../../contexts/LayoutContextProvider'
import { genCsv } from '../../utils/csv'
import { search } from '../../utils/search'
import { typeObj, statusObj, payStatusObj } from '../../const/fund'
import { isAllowed } from '../../route/roles'
import { courseObj } from '../../const/student'

const List = () => {
  const navigate = useNavigate()
  const { db, role } = useAuth()
  const { type } = useParams()
  const { getPageNo, onPageChange, getSearch, setSearch, getLenders, getCoops, getCompanies } = useLayoutContext()

  const [updateCount, setUpdateCount] = useState(0)
  const [initValue, setInitValue] = useState([])
  const [value, setValue] = useState([])
  const [qsVisible, setQsVisible] = useState(false)
  const [qs, setQs] = useState({})
  const [tags, setTags] = useState([])

  const [coopObj, setCoopObj] = useState()
  const [companyObj, setCompanyObj] = useState()
  const [lenderObj, setLenderObj] = useState()

  const [searchForm] = AntdForm.useForm()

  const { loading } = useAsync(async () => {
    const coops = await getCoops()
    const coopData = coops.reduce((acc, o) => {
      acc[o.id] = o
      return acc
    }, {})
    setCoopObj(coopData)

    const companies = await getCompanies()
    const companyData = companies.reduce((acc, o) => {
      acc[o.id] = o
      return acc
    }, {})
    setCompanyObj(companyData)

    const lenders = await getLenders()
    const lenderData = lenders.reduce((acc, o) => {
      acc[o.id] = o
      return acc
    }, {})
    setLenderObj(lenderData)

    const snap = await db.collection('/funds').where('type', '==', type).get()
    const data = snap.docs.map(doc => {
      const d = doc.data()
      return {
        ...d,
        id: doc.id
      }
    })

    const q = getSearch('funds')
    setQs(q || {})
    setInitValue(data)
  }, [type, updateCount])

  useEffect(() => {
    const tagList = Object.keys(qs).reduce((acc, o) => {
      return (!qs[o]) ? acc : [...acc, {
        key: o,
        value: (o === 'lid') ? lenderObj[qs[o]]?.name : (o === 'paystatus') ? payStatusObj[qs[o]]?.name : (o === 'status') ? statusObj[qs[o]]?.name : qs[o],
        label: {
          scn: 'รหัสนักเรียน',
          firstname: 'ชื่อ',
          lastname: 'นามสกุล',
          lid: 'ผู้ให้กู้',
          paystatus: 'สถานะชำระล่าสุด',
          status: 'สถานะ'
        }[o]
      }]
    }, [])
    setTags(tagList)
    const data = search(initValue, qs)
    setValue(data)
  }, [initValue, qs, lenderObj])

  const handleClose = (key) => {
    const newQs = {
      ...qs,
      [key]: null
    }
    setQs(newQs)
    setSearch('funds', newQs)
    searchForm.setFieldsValue(newQs)
  }

  const handleDelete = async (row) => {
    try {
      await db.collection('/funds').doc(row.id).delete()
      setUpdateCount(updateCount + 1)
      return Promise.resolve()
    } catch (error) {
      return Promise.reject(error)
    }
  }

  const download = async (data) => {
    const list = data.map((o) => ({
      'รหัสนักเรียน': o?.scn ?? '',
      'ชื่อ': o?.firstname ?? '',
      'นามสกุล': o?.lastname ?? '',
      'ผู้ให้กู้': lenderObj[o?.lid]?.name ?? '',
      'สถานะ': statusObj[o?.status]?.name ?? ''
    }))
    try {
      await genCsv('fund_list', list)
      return Promise.resolve()
    } catch (error) {
      return Promise.reject(error)
    }
  }

  const columns = [
    {
      title: 'รหัสนักเรียน',
      dataIndex: 'scn',
      width: 120
    },
    {
      title: 'ชื่อ',
      dataIndex: 'firstname',
      width: 150
    },
    {
      title: 'นามสกุล',
      dataIndex: 'lastname',
      width: 150
    },
    {
      title: 'หลักสูตร',
      dataIndex: 'course',
      width: 150,
      render: data => courseObj[data]?.name ?? ''
    },
    {
      title: 'บริษัท',
      dataIndex: 'companyid',
      width: 150,
      render: data => companyObj[data]?.name ?? ''
    },
    {
      title: 'สหกรณ์',
      dataIndex: 'coopid',
      width: 150,
      render: data => coopObj[data]?.name ?? ''
    },
    {
      title: 'ผู้ให้กู้',
      dataIndex: 'lid',
      width: 150,
      render: data => lenderObj[data]?.name ?? ''
    },
    {
      title: 'สถานะการชำระเงิน',
      dataIndex: 'paystatus',
      width: 150,
      render: data => (
        <span style={{ color: payStatusObj[data]?.color ?? '' }}>{payStatusObj[data]?.name ?? ''}</span>
      )
    },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      width: 120,
      render: data => (
        <span style={{ color: statusObj[data]?.color ?? '' }}>{statusObj[data]?.name ?? ''}</span>
      )
    },
    {
      title: 'เครื่องมือ',
      dataIndex: 'Operation',
      width: 100,
      render: (_, row) => (
        <div>
          <Button
            type='link'
            style={{ marginRight: '8px' }}
            icon={<EyeOutlined style={{ fontSize: 18 }} />}
            onClick={() => {
              navigate(`/admin/funds/${type}/${row.id}/view`)
            }}
          ></Button>
          {(isAllowed(role, 'delete', 'funds', type)) && (
            <Popconfirm
              title='Are you sure to delete this room?'
              onConfirm={() => handleDelete(row)}
              okText='Yes'
              cancelText='No'
            >
              <Button
                type='link'
                style={{ marginRight: '8px' }}
                danger
                icon={<DeleteOutlined style={{ fontSize: 18 }} />}
              ></Button>
            </Popconfirm>
          )}
        </div>
      )
    }
  ]

  if (loading) return <Skeleton></Skeleton>
  return (
    <Card style={{ minHeight: '100%' }}>
      <Row>
        <Col flex='auto'>
          <div style={{ fontSize: 24 }}>
            {typeObj[type]?.name ?? ''}
          </div>
          <div style={{ marginTop: 12 }}>
            {tags.map((o) => (
              <Tag
                key={o.key}
                closable
                onClose={() => handleClose(o.key)}
              >
                {o.label}: {o.value}
              </Tag>
            ))}
          </div>
        </Col>
        <Col flex='300px'>
          <div style={{ float: 'right', margin: '0 0 16px 0' }}>
            {(isAllowed(role, 'create', 'funds', type)) && (
              <div>
                <Button
                  type='primary'
                  icon={<PlusOutlined style={{ fontSize: 14 }} />}
                  onClick={() => {
                    navigate(`/admin/funds/${type}/create`)
                  }}
                >
                  สร้างลูกหนี้
                </Button>
              </div>
            )}
            <div style={{ float: 'right', margin: '16px 0 16px 0' }}>
              <Button
                icon={<SearchOutlined />}
                style={{ margin: '0 8px' }}
                onClick={() => {
                  setQsVisible(true)
                }}
              ></Button>
              <Button
                icon={<PrinterOutlined />}
                onClick={() => {
                  download(value)
                }}
              ></Button>
            </div>
          </div>
        </Col>
      </Row >
      <Table
        bordered
        rowKey='id'
        style={{ overflowX: 'auto' }}
        columns={columns}
        dataSource={value}
        pagination={{
          total: value.length,
          current: getPageNo('funds'),
          onChange(page) {
            onPageChange('funds', page)
          }
        }}
      />
      <Modal
        forceRender
        open={qsVisible}
        title={
          <div style={{ padding: '16px 24px', fontWeight: 'bold', fontSize: '18px', lineHeight: '23px', backgroundColor: '#228B90', color: '#F6F6F6', borderRadius: '5px 5px 0px 0px' }}>
            ค้นหา
          </div>
        }
        width='50%'
        closable={false}
        footer={
          <Row justify='center'>
            <Space align='center'>
              <Col>
                <Button
                  type='secondary'
                  size='medium'
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    const empty = {
                      scn: null,
                      firstname: null,
                      lastname: null,
                      lid: null,
                      paystatus: null,
                      status: null
                    }
                    setQs(empty)
                    setSearch('funds', empty)
                    searchForm.setFieldsValue(empty)
                  }}
                >
                  Clear Data
                </Button>
              </Col>
              <Col>
                <Button
                  size='medium'
                  style={{ color: '#FFF', backgroundColor: '#EE7930' }} icon={<CloseOutlined />}
                  onClick={() => {
                    setQsVisible(false)
                  }}
                >
                  ยกเลิก
                </Button>
              </Col>
              <Col>
                <Button
                  type='primary'
                  size='medium'
                  icon={<CheckOutlined />}
                  onClick={() => {
                    const data = searchForm.getFieldsValue()
                    setQs(data)
                    setSearch('funds', data)
                    setQsVisible(false)
                  }}
                >
                  ยืนยัน
                </Button>
              </Col>
            </Space>
          </Row>
        }
      >
        <AntdForm
          layout='vertical'
          initialValues={qs}
          style={{ margin: '8px 32px' }}
          form={searchForm}
          name='searchForm'
        >
          <Row gutter={[24, 24]}>
            <Col span={12}>
              <AntdForm.Item name='scn' label='รหัสนักเรียน' >
                <Input placeholder='รหัสนักเรียน' />
              </AntdForm.Item>
            </Col>
            <Col span={12}>
              <AntdForm.Item name='lid' label='ผู้ให้กู้' >
                <Select placeholder='เลือก' >
                  {((Object.keys(lenderObj)).map((o) => (
                    <Select.Option key={o} value={o}>{lenderObj[o].name}</Select.Option>
                  )))}
                </Select>
              </AntdForm.Item>
            </Col>
          </Row>
          <Row gutter={[24, 24]}>
            <Col span={12}>
              <AntdForm.Item name='firstname' label='ชื่อ' >
                <Input placeholder='ชื่อ' />
              </AntdForm.Item>
            </Col>
            <Col span={12}>
              <AntdForm.Item name='lastname' label='นามสกุล' >
                <Input placeholder='นามสกุล' />
              </AntdForm.Item>
            </Col>
          </Row>
          <Row gutter={[24, 24]}>
            <Col span={12}>
              <AntdForm.Item name='paystatus' label='สถานะการชำระเงิน' >
                <Select placeholder='เลือก' >
                  {((Object.keys(payStatusObj)).map((o) => (
                    <Select.Option key={o} value={o}>{payStatusObj[o].name}</Select.Option>
                  )))}
                </Select>
              </AntdForm.Item>
            </Col>
            <Col span={12}>
              <AntdForm.Item name='status' label='สถานะ' >
                <Select placeholder='เลือก' >
                  {((Object.keys(statusObj)).map((o) => (
                    <Select.Option key={o} value={o}>{statusObj[o].name}</Select.Option>
                  )))}
                </Select>
              </AntdForm.Item>
            </Col>
          </Row>
        </AntdForm>
      </Modal>
    </Card >
  )
}

export default List
